import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AppDispatch } from 'store'
import { Input } from '../../../components/Input/Input'
import { NavBar } from '../../../components/NavBar/NavBar'
import {
    Flex,
    ItemsGrid,
    Button,
    Separator,
    H1WithBack,
    PageWrapper,
    PageFooter,
    ThinSeparator,
    SquareIcon,
    Box,
} from '../../../styling/GlobalStyles'
import { CancelBtn } from 'components/Button/CancelBtn'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { ISelectValue } from 'constants/globalTypes'
import useFocusFirstInput from 'utils/useFocusFirstInput'
import { parseLocationRegistryOptions } from 'features/locationRegistry/locationRegistryMappers'
import { addInventoryReceipt } from 'features/inventory/inventoryReceiptsSlice'
import { IAddInventoryReceiptState } from 'types/inventory.interface'
import { IAddReceiptQcState } from 'types/inventorySupply.interface'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { PlusIcon } from 'assets/icons/PlusIcon'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { lifeCycleStartSources, lifeCycleTypes } from '../Items/itemsData'
import { DropZone } from 'components/DropZone/DropZone'
import { receiptQcResultOptions } from './receiptsData'

export const AddReceipt = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch<AppDispatch>()
    const [file, setFile] = useState<File>(null)
    const [qc, setQc] = useState<IAddReceiptQcState>({
        checkedBy: '',
        result: null,
        reason: '',
        orderedQty: null,
        receivedQty: null,
    })
    const [state, setState] = useState<IAddInventoryReceiptState>({
        supplier: null,
        receiptNo: '',
        receiptDate: null,
        location: null,
        currency: '',
        notes: '',
        batches: [{
            id: new Date().getMilliseconds(),
            item: null,
            batchCode: '',
            quantity: null,
            unitCost: null,
            unit: null,
            mfgDate: null,
            expiryDate: null,
            densityKgPerL: null,
            expiryValue: null,
            startSourceDate: null,
            serials: []
        }]
    })

    useFocusFirstInput()

    const handleSubmit = async () => {
        if (!state.receiptDate || state.batches.length === 0 || state.batches.some(b => !b.item?.value || !b.quantity || !b.unit?.value || !b.unitCost)) {
            toast.warn('Receipt date and batches are required')
            return
        }

        if (state.batches.some(b => b.item?.serial && b.serials.some(s => !s.serialNo))) {
            toast.warn('All serials must have serial number ')
            return
        }

        //  todo validacija za lifecyccle

        const resp = await dispatch(addInventoryReceipt({ data: state, file }))
        if (resp.meta.requestStatus === 'fulfilled') {
            navigate('/inventory/receipts')
        }
    }

    const handleSelect = (value: ISelectValue, name: string) => {
        setState({ ...state, [name]: value })
    }


    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setState(prev => ({ ...prev, [e.target.name]: e.target.value }))
    }

    const handleQcChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, valueAsNumber, type } = e.target
        setQc(prev => ({
            ...prev,
            [name]: type === 'number' ? (Number.isNaN(valueAsNumber) ? null : valueAsNumber) : value,
        }))
    }

    const qcDelta = qc.orderedQty != null && qc.receivedQty != null
        ? qc.receivedQty - qc.orderedQty
        : null

    const addBatch = () => {
        setState(prev => ({
            ...prev, batches: [...prev.batches, {
                id: new Date().getMilliseconds(),
                item: null,
                batchCode: '',
                quantity: null,
                unitCost: null,
                unit: null,
                mfgDate: null,
                expiryDate: null,
                densityKgPerL: null,
                expiryValue: null,
                startSourceDate: null,
                serials: []
            }]
        }))
    }

    const handleBatchSelect = (v: ISelectValue, n: string, id: number) => {
        setState(prev => ({
            ...prev, batches: prev.batches.map(b => {
                if (b.id !== id) {
                    return b
                } else {
                    return { ...b, [n]: v, }
                }
            })
        }))
    }

    const handleBatchChange = (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
        const { value, name, valueAsNumber } = e.target
        setState(prev => ({
            ...prev, batches: prev.batches.map(b => {
                if (b.id !== id) {
                    return b
                } else {
                    return { ...b, [name]: name == 'batchCode' ? value : name == 'quantity' && valueAsNumber > 20 ? 20 : valueAsNumber }
                }
            })
        }))
    }

    const handleBatchDate = (date: Date, name: string, id: number) => {
        setState(prev => ({
            ...prev, batches: prev.batches.map(b => {
                if (b.id !== id) {
                    return b
                } else {
                    return { ...b, [name]: date }
                }
            })
        }))
    }

    const removeBatch = (id: number) => {
        setState(prev => ({
            ...prev, batches: prev.batches.filter(b => b.id !== id)
        }))
    }

    const handleBatchSerial = (e: React.ChangeEvent<HTMLInputElement>, batchId: number, serialId: number) => {
        const { value, name } = e.target
        setState(prev => ({
            ...prev, batches: prev.batches.map(b => {
                if (b.id !== batchId) {
                    return b
                } else {
                    return {
                        ...b, serials: b.serials.map(s => {
                            if (s.id !== serialId) {
                                return s
                            } else {
                                return { ...s, [name]: value }
                            }
                        })
                    }
                }
            })
        }))
    }

    const handleBatchSerialDate = (date: Date, batchId: number, serialId: number) => {
        setState(prev => ({
            ...prev, batches: prev.batches.map(b => {
                if (b.id !== batchId) {
                    return b
                } else {
                    return {
                        ...b, serials: b.serials.map(s => {
                            if (s.id !== serialId) {
                                return s
                            } else {
                                return { ...s, warrantyExpiry: date }
                            }
                        })
                    }
                }
            })
        }))
    }


    const handleQuantity = (e: React.ChangeEvent<HTMLInputElement>, batchId: number) => {
        const { valueAsNumber } = e.target
        if (valueAsNumber > 20) {
            toast.warn('Max quantity for a batch with serial tracking is 20')
        } else {
            setState(prev => ({
                ...prev, batches: prev.batches.map(b => {
                    if (b.id !== batchId) {
                        return b
                    } else {
                        return { ...b, serials: Array.from({ length: valueAsNumber > 20 ? 20 : valueAsNumber }, (_, i) => ({ id: new Date().getMilliseconds() + i, serialNo: '', warrantyExpiry: null })) }
                    }
                })
            }))
        }
    }

    return (
        <>
            <NavBar />
            <PageWrapper>
                <H1WithBack title='Add Receipt' arrow />
                <Separator />
                <ItemsGrid columns={3} width='100%'>
                    <HonestSelectAsyncInput
                        link='inventory-suppliers'
                        name='supplier'
                        labelText='Supplier'
                        placeholder='Select Supplier'
                        defaultValue={state?.supplier?.label}
                        onChange={handleSelect}
                        parserFunction={(data: any) => data.data.map((i: any) => ({ label: i.name, value: i.id }))}
                        nomarg
                    />
                    <Input
                        nomarg
                        type='text'
                        name='receiptNo'
                        labelText='Receipt Number'
                        placeholder='1341341234'
                        value={state.receiptNo}
                        onChange={handleChange}
                    />
                    <CalendarInput
                        labelText='Receipt Date'
                        defaultValue={state.receiptDate}
                        onChange={(v) => setState(prev => ({ ...prev, receiptDate: v }))}
                        required
                        width='100%'
                        nomarg
                    />
                    <HonestSelectAsyncInput
                        nomarg
                        link='locations'
                        placeholder='Search location'
                        name='location'
                        labelText='Location'
                        defaultValue={state?.location?.label}
                        onChange={handleSelect}
                        parserFunction={parseLocationRegistryOptions}
                    />
                    <Input
                        nomarg
                        type='text'
                        name='currency'
                        labelText='Currency'
                        placeholder='Ghc'
                        value={state.currency}
                        onChange={handleChange}
                    />

                </ItemsGrid>
                <ThinSeparator />
                <Flex gap='1rem' between width={screen.availWidth < 1000 ? '100%' : '50%'} >
                    <Input
                        type='textarea'
                        minHeight='150px'
                        placeholder='Enter notes'
                        onChange={handleChange}
                        nomarg
                        name='notes'
                        labelText='Notes'
                        value={state.notes}
                    />
                    <Flex justcenter column gap='1rem' >
                        <BodyText style={{ marginTop: '2rem' }} >Add attachment</BodyText>
                        <DropZone acceptAll file={file} setFile={setFile} />
                    </Flex>
                </Flex>

                <ThinSeparator />
                <Flex center gap='2rem' >
                    <TitleText weight='bold' >Batches:</TitleText>
                    <SquareIcon style={{ marginTop: '1rem' }} onClick={addBatch}  >
                        {PlusIcon}
                    </SquareIcon>
                </Flex>
                {state.batches.length ? <Flex mt='16' column width='100%' gap='1rem' >{state.batches.map((b, i) => <Box width='100%' key={b.id} >
                    <Flex width='100%' gap='1rem' wrap >
                        <Flex width='100%' between >
                            <TitleText weight='bold' >Batch {i + 1}</TitleText>
                            <CloseIcon onClick={() => removeBatch(b.id)} />
                        </Flex>
                        <Flex gap='1rem' wrap  >
                            <HonestSelectAsyncInput
                                name='item'
                                labelText='Item'
                                onChange={(v, n) => handleBatchSelect(v, n, b.id)}
                                link='inventory/item'
                                defaultValue={b.item?.label}
                                parserFunction={(data: any) => data.data.map((i: any) => ({
                                    label: i.name, value: i.id, serial: i.trackingMode == 4,
                                    lifecycleExpirationType: lifeCycleTypes.find(t => t.value == i.lifecycleExpirationType),
                                    lifecycleStartSourceType: lifeCycleStartSources.find(s => s.value == i.lifecycleStartSourceType)
                                }))}
                                invalid={!b.item?.value}
                                width='200px'
                            />
                            {
                                !!b.item?.lifecycleExpirationType?.value && b.item?.lifecycleExpirationType?.label == 'Expiry Date' ? <CalendarInput
                                    labelText='Expiry Date'
                                    defaultValue={b.expiryDate}
                                    onChange={d => handleBatchDate(d, 'expiryDate', b.id)}
                                    required
                                    width='200'

                                /> :
                                    <Input width='200' type='number' name='expiryValue' labelText='Expiry Value' placeholder='30' value={b.expiryValue} onChange={e => handleBatchChange(e, b.id)} />
                            }
                            {
                                !!b.item?.lifecycleStartSourceType?.value && <CalendarInput
                                    labelText='Start Source Date'
                                    defaultValue={b.startSourceDate}
                                    onChange={d => handleBatchDate(d, 'startSourceDate', b.id)}
                                    required
                                    width='200'

                                />
                            }
                            <Input
                                type='text'
                                placeholder='C1443'
                                onChange={e => handleBatchChange(e, b.id)}
                                name='batchCode'
                                labelText='Batch Code'
                                value={b.batchCode}
                                width='200'

                            />
                            <Input
                                type='number'
                                placeholder='12'
                                onChange={e => handleBatchChange(e, b.id)}
                                name='quantity'
                                labelText='Quantity'
                                value={b.quantity}
                                invalid={!b.quantity}
                                onBlur={e => handleQuantity(e, b.id)}
                                required
                                width='200'

                            />
                            <HonestSelectAsyncInput
                                name='unit'
                                link='units'
                                onChange={(v, n) => handleBatchSelect(v, n, b.id)}
                                defaultValue={b.unit?.label}
                                labelText='Unit'
                                parserFunction={(data: any) => data.map((i: any) => ({ label: i.name, value: i.id }))}
                                invalid={!b.unit?.value}
                                required
                                width='200px'

                            />
                            <Input
                                type='number'
                                placeholder='124'
                                onChange={e => handleBatchChange(e, b.id)}
                                name='unitCost'
                                labelText='Unit Cost'
                                value={b.unitCost}
                                invalid={!b.unitCost}
                                required
                                width='200'

                            />
                            <Input
                                type='number'
                                placeholder='124'
                                onChange={e => handleBatchChange(e, b.id)}
                                name='densityKgPerL'
                                labelText='Density Kg Per L'
                                value={b.densityKgPerL}
                                width='200'

                            />
                            <CalendarInput
                                labelText='MFG Date'
                                defaultValue={b.mfgDate}
                                onChange={d => handleBatchDate(d, 'mfgDate', b.id)}
                                width='200'
                            />
                        </Flex>
                        {b.item?.serial && !!b.serials.length && <>
                            <ThinSeparator />
                            <TitleText weight='bold' >Serials:</TitleText>
                            <Flex width='100%' gap='1rem' wrap>
                                {
                                    b.serials.map((s, i) => <Box key={s.id}  >
                                        <Flex mb='16' width='100%' between >
                                            <BodyText weight='bold' >Serial {i + 1}</BodyText>
                                        </Flex>
                                        <Input
                                            name='serialNo'
                                            labelText='Serial Number'
                                            placeholder='134124123'
                                            value={s.serialNo}
                                            onChange={e => handleBatchSerial(e, b.id, s.id)}
                                        />
                                        <CalendarInput
                                            labelText='Warranty Expiry'
                                            defaultValue={s.warrantyExpiry}
                                            onChange={d => handleBatchSerialDate(d, b.id, s.id)}
                                        />
                                    </Box>)
                                }

                            </Flex>
                        </>}
                    </Flex>
                </Box>)}</Flex> : <BodyText>No Batches</BodyText>}

                <ThinSeparator />
                <Flex center gap='1rem' wrap >
                    <TitleText weight='bold' >Quality check:</TitleText>
                </Flex>
                <ItemsGrid mt='1rem' columns={3} width='100%'>
                    <Input
                        nomarg
                        type='text'
                        name='checkedBy'
                        labelText='Checked By'
                        placeholder='Enter inspector name'
                        value={qc.checkedBy}
                        onChange={handleQcChange}
                    />
                    <HonestSelectInput
                        nomarg
                        options={receiptQcResultOptions}
                        labelText='QC Result'
                        name='result'
                        placeholder='Select result'
                        onChange={(v: ISelectValue) => setQc(prev => ({ ...prev, result: v }))}
                    />
                    {qc.result?.value === 3 && (
                        <Input
                            nomarg
                            type='text'
                            name='reason'
                            labelText='Rejection Reason'
                            placeholder='Enter reason'
                            value={qc.reason}
                            onChange={handleQcChange}
                            invalid={!qc.reason}
                            required
                        />
                    )}
                    <Input
                        nomarg
                        type='number'
                        name='orderedQty'
                        labelText='Ordered Quantity'
                        placeholder='2'
                        value={qc.orderedQty}
                        onChange={handleQcChange}
                    />
                    <Input
                        nomarg
                        type='number'
                        name='receivedQty'
                        labelText='Received Quantity'
                        placeholder='2'
                        value={qc.receivedQty}
                        onChange={handleQcChange}
                    />
                </ItemsGrid>
                {qcDelta != null && (
                    <Flex mt='16'>
                        <OtherText color={qcDelta < 0 ? tokens.status.danger.fg : tokens.status.success.fg}>
                            {qcDelta === 0 ? 'Received matches ordered' : `Δ ${qcDelta > 0 ? '+' : ''}${qcDelta} vs ordered`}
                        </OtherText>
                    </Flex>
                )}
            </PageWrapper>
            <PageFooter>
                <Flex gap='1rem'>
                    <CancelBtn />
                    <Button orange onClick={handleSubmit}>
                        Save
                    </Button>
                </Flex>
            </PageFooter>
        </>
    )
}
